import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm, UntypedFormControl } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { faCheck, faCubes, faPlus, faPlusCircle, faSliders, faXmark } from '@fortawesome/free-solid-svg-icons';
import { forkJoin, Observable } from 'rxjs';
import * as _ from 'underscore';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { MockSharedService, SHARED_SERVICE } from '../../../../../../shared/mockSharedService';
import { TenantWordsPipe } from '../../../../../core/pipes/tenant-words.pipe';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { PurchaseOrder } from '../../../../purchase-order/models/purchaseOrder';
import { HandlingUnitDetailDTO } from '../../../models/handling-unit-detail';
import { HandlingUnitMasterDTO, handlingUnitLocation } from '../../../models/handling-unit-master';
import { InventoryLotDTO } from '../../../models/inventory-lot';
import { InventoryItemDTO } from '../../../models/item';
import { InventoryItemEditComponent } from '../inventory-item-edit/inventory-item-edit.component';

export interface InventoryItemPopupData {
  item?: InventoryItemDTO;
  hum?: HandlingUnitMasterDTO;
  hud?: HandlingUnitDetailDTO;
  isHumEdit?: boolean;
  isPopup?: boolean;
  isReturnBottomsheet?: boolean;
  isBulkCreate?: boolean;
}

export interface InventoryLotPopupData {
  lot?: InventoryLotDTO;
  hum?: HandlingUnitMasterDTO;
  hud?: HandlingUnitDetailDTO;
  isPopup?: boolean;
  isHumEdit?: boolean;
  isReturnBottomsheet?: boolean;
  isBulkCreate?: boolean;
}
 
export interface InventoryAddItemData {
  Hum: HandlingUnitMasterDTO;
  hud?: HandlingUnitDetailDTO;
}

@Component({
  selector: 'inventory-add-item-popup',
  templateUrl: './inventory-add-item-popup.component.html',
  styleUrls: ['./inventory-add-item-popup.component.scss']
})
export class InventoryAddItemPopupComponent implements OnInit {
  @Input() isHumEdit: boolean = false;
  @Input() hum: HandlingUnitMasterDTO;
  @Input() isBulkCreate: boolean = false;

  @Input() itemId: number;
  @Input() lotId: number;
  @Input() items: PurchaseOrder;
  @ViewChild('itemForm', { static: true }) public itemForm: NgForm;

  static open(dialog: MatDialog, data: InventoryAddItemData): MatDialogRef<InventoryAddItemPopupComponent, HandlingUnitMasterDTO> {
    return dialog.open(InventoryAddItemPopupComponent, { data, width: '860px', maxWidth: '95vw' });
  }

  // Inline in Create Inventory these resolve to that dialog's ref and data; neither is used there.
  constructor(public dialogRef: MatDialogRef<InventoryAddItemPopupComponent, HandlingUnitMasterDTO>,
    @Inject(MAT_DIALOG_DATA) public data: InventoryAddItemData,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private changeDetRef: ChangeDetectorRef,
    private router: Router,
    private pipe: TenantWordsPipe,
    private inventoryService: InventoryService,
    @Inject(SHARED_SERVICE) private sharedService: MockSharedService, //Inject interface of shared service
    ) { }


  public faXmark = faXmark;
  public faCubes = faCubes;
  public faSliders = faSliders;
  public faCheck = faCheck;

  public isLoading: boolean = true;

  private destroyRef = inject(DestroyRef);

  public qty: number;
  public productDescription: string;
  public packageQty: number;
  public packageComponentQty: number;

  public hud: HandlingUnitDetailDTO = {};
  public hudReady: boolean = false;
  public faPlusCircle = faPlusCircle;

  public selectedItem: InventoryItemDTO;
  public searchItem = "";
  public allItems: InventoryItemDTO[] = [];
  public filteredItemOptions: InventoryItemDTO[] = [];
  public defaultItemSearchPrompt = `Please search for a ${this.pipe.transform("item")}.`;
  public itemSearchPrompt: string = `Please search for a ${this.pipe.transform("item")}.`;

  public selectedLot: InventoryLotDTO;
  public searchLot = "";
  public allLots: InventoryLotDTO[] = [];
  public filteredLotOptions: InventoryLotDTO[] = [];
  public defaultLotSearchPrompt = `Please search for a ${this.pipe.transform("lot")}.`;
  public lotSearchPrompt: string = `Please search for a ${this.pipe.transform("lot")}.`;


  public productControl = new UntypedFormControl();
  public filteredProductOptions: Observable<InventoryItemDTO[]>;

  faPlus = faPlus;

  ngOnChanges(changes: SimpleChanges): void {
    this.filterItembyPOItems();
  }
  
  ngOnInit() {
    this.hud = {};
    // Own object inline, so Create Inventory's dialog data isn't overwritten.
    if (this.hum)
      this.data = { Hum: this.hum };
    this.getData();

    if (this.data.hud) {
      this.hud.itemId = this.data.hud.itemId
      this.hud.lotId = this.data.hud.lotId
    }

  }

  getData() {
    this.isLoading = true;
    forkJoin([
      this.inventoryService.getInventoryItems(),
      // this.inventoryService.getLots()
      this.inventoryService.getOnlyLots()
      //this.roomService.getRooms()
    ])
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
        this.allItems = x[0];
        this.allLots = x[1];

        
        if(this.hud) {
          this.searchLot = this.hud.lotNumber;
          this.lotId = this.hud.lotId;
          this.itemId = this.hud.itemId;
          this.searchItem = this.hud.itemName;
        }
        this.filterItembyPOItems();
        this.filterLots();
        this.filterItems();
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }, error:err => {
        this.errorService.handleError(err);
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }});


  }


  close() {
    this.dialogRef.close();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  addItem() {
    const data: InventoryItemPopupData = {
      item: {},
      hum: this.data.Hum,
      hud: this.hud,
      isHumEdit: this.isHumEdit,
      isPopup: true,
      isReturnBottomsheet: true,
      isBulkCreate: this.isBulkCreate
    };
    InventoryItemEditComponent.open(this.dialog, data)
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getData());
  }

  addItemToHum() {
    this.hud.itemName = this.allItems.find(x => x.id == this.hud.itemId).name;
    this.hud.quantity = this.qty;
    this.hud.isActive = true;
    this.hud.handlingUnitMasterId = this.data.Hum.id;
    this.hud.packageComponentWeightUnitOfMeasure = this.allItems.find(x => x.id == this.hud.itemId).packageComponentWeightUnitOfMeasure;
    this.hud.itemDescription = this.productDescription;
    this.hud.packageComponentWeight = this.allItems.find(x => x.id == this.hud.itemId).packageComponentWeight;
    this.hud.packageComponentQuantityPerPackage = this.allItems.find(x => x.id == this.hud.itemId).packageComponentQuantityPerPackage;
    if (!this.hum.handlingUnitDetails)
      this.data.Hum.handlingUnitDetails = [];
    this.hum.handlingUnitDetails.push({ ...this.hud });
    this.hud = {};
    this.qty = 0;
    this.packageComponentQty = 0;
    this.packageQty = 0;
    this.itemForm.reset();
  }

  get fieldAppearance(): MatFormFieldAppearance {
    return this.isHumEdit ? 'fill' : 'outline';
  }

  get canAdd(): boolean {
    return !!this.qty && !!this.lotId && !!this.itemId;
  }

  get showSummary(): boolean {
    const item = this.selectedItem;
    return !!item?.id && (item.packageHasComponents || item.packageQuantityPerPallet > 1 || item.packageComponentWeight != 0);
  }

  get subtitle(): string {
    return [this.data.Hum?.barcode, handlingUnitLocation(this.data.Hum)].filter(Boolean).join(' · ');
  }

  getBoxQty() {
    if (this.selectedItem && this.selectedItem.packageComponentQuantityPerPackage != 0)
      return Math.ceil((this.qty / this.selectedItem.packageComponentQuantityPerPackage));
    else
      return 0;
  }

  getPalletQty() {
    if (this.selectedItem && this.selectedItem.packageQuantityPerPallet != 0)
      return Math.ceil((this.getBoxQty() / this.selectedItem.packageQuantityPerPallet));
    else
      return 0;
  }

  filterLots() {

    if (this.allLots) {
      if (!this.searchLot)
        this.searchLot = "";

      let filterValue = this.searchLot.toLowerCase();
      let results = this.allLots.filter(option => option.lotNumber && option.lotNumber.toLowerCase().includes(filterValue));

      if (results.length > 40 && !this.lotId) {
        this.filteredLotOptions = [];
        this.lotSearchPrompt = `Too many ${this.pipe.transform("lots")} found... please narrow your search.`
      } else {
        this.filteredLotOptions = _.sortBy([...results], x => x.lotNumber);
        this.lotSearchPrompt = this.defaultLotSearchPrompt;
      }
    }
  }

  filterItems() {

    if (this.allItems) {

      if (!this.searchItem)
        this.searchItem = "";

      let filterValue = this.searchItem.toLowerCase();
      let results = this.allItems.filter(option => option.name && option.name.toLowerCase().includes(filterValue));

      if (results.length > 40 && !this.itemId) {
        this.filteredItemOptions = [];
        this.itemSearchPrompt = `Too many ${this.pipe.transform("items")} found... please narrow your search.`
      } else {
        this.filteredItemOptions = _.sortBy([...results], x => x.name);
        this.itemSearchPrompt = this.defaultLotSearchPrompt;
      }
    }
  }


  addLot() {

    const data: InventoryLotPopupData = {
      lot: {},
      isPopup: true,
      hum: this.data.Hum,
      isHumEdit: this.isHumEdit,
      hud: this.hud,
      isReturnBottomsheet: true,
      isBulkCreate: this.isBulkCreate
    };

    // open the lot edit dialog
    let ref = this.sharedService.openDialog('InventoryLotEditComponent', this.dialog, { data, width: '900px', maxWidth: '95vw' });


    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getData());
  }

  submit() {
    this.isLoading = true;
    this.hud.itemName = this.allItems.find(x => x.id == this.hud.itemId).name;
    this.hud.quantity = this.qty;
    this.hud.isActive = true;
    this.hud.handlingUnitMasterId = this.data.Hum.id
    this.data.Hum.handlingUnitDetails.push(this.hud);
    this.inventoryService.putInventoryHUM(this.data.Hum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {

      this.dialogRef.close(x);

    }, error:err => {
      this.errorService.handleError(err);
      this.isLoading = false;
    }});
  }

  itemSelectionChanged() {
    this.selectedItem = this.allItems.find(x => x.id == this.itemId);
    this.productDescription = this.selectedItem.description;
    
    if (this.selectedItem) {
      this.hud.itemId = this.selectedItem.id;
      this.hud.itemName = this.selectedItem.name;
    }
    else{
      this.hud.itemId = null;
      this.hud.itemName = null;
    }

    if((this.qty || this.packageComponentQty) && this.hud.lotId && this.hud.itemId)
      this.hudReady = true;
    else
      this.hudReady = false;

    this.changeDetRef.detectChanges();
  }

  lotSelectionChanged() {
    let selectedLot = this.allLots.find(x => x.id == this.lotId);

    if (selectedLot) {
      this.hud.lotId = selectedLot.id;
      this.hud.lotNumber = selectedLot.lotNumber;
    }
    else{
      this.hud.lotId = null;
      this.hud.lotNumber = null;
    }

    if((this.qty || this.packageComponentQty) && this.hud.lotId && this.hud.itemId)
      this.hudReady = true;
    else
      this.hudReady = false;
  }




  setItem(item?: InventoryItemDTO) {
    if (item) {
      this.selectedItem = item;
      this.hud.itemId = item.id
      this.hud.itemName = item.name;
    }

    if((this.qty || this.packageComponentQty) && this.hud.lotId && this.hud.itemId)
      this.hudReady = true;
    else
      this.hudReady = false;
  }

  getWeight() {
    if(this.selectedItem)
      return this.qty * this.selectedItem.packageComponentQuantityPerPackage * this.selectedItem.packageComponentWeight;
    else
      return 0;
  }

  setQty() {
    if(this.selectedItem && this.selectedItem.packageHasComponents) {
      let componentToPackage =  this.packageComponentQty/this.selectedItem.packageComponentQuantityPerPackage;

      if(!this.packageQty)
        this.packageQty = 0;

      if(componentToPackage)
        this.qty = componentToPackage + this.packageQty;
      else
        this.qty = this.packageQty;

    }

    if((this.qty || this.packageComponentQty) && this.hud.lotId && this.hud.itemId)
      this.hudReady = true;
    else
      this.hudReady = false;
  }

  setDescription() {
    if(this.productDescription) {
      this.hud.itemDescription = this.productDescription;
    }


  }

  readyCheck(){
    if((this.qty || this.packageComponentQty) && this.hud.lotId && this.hud.itemId)
      this.hudReady = true;
    else
      this.hudReady = false;
  }

  filterItembyPOItems(){
    if (!!this.items && this.allItems.length > 0) {
      let selectedItems = this.items.items.map(x=>x.itemId) || [];
      this.allItems = this.allItems.filter(x=> selectedItems.includes(x.id));
    }
  }
}
